import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import {
  Cleanup,
  createInstallDatadogErrorForwardingFunction,
  extractConsoleArgs,
  getDdLogProperties,
  LogLevel,
  LogSource,
  normalizeUnknownError,
  SendToDataDogFunction,
} from './dataDogSharedLib';

// *************************************************************
// Error Reporting
// *************************************************************

const sendToDataDog: SendToDataDogFunction = (
  level: LogLevel,
  source: LogSource,
  params: unknown[],
) => {
  const { error, message, contextArg } = extractConsoleArgs(params);
  datadogLogs.logger[level](message, getDdLogProperties(level, source, error, contextArg));
  if (level === 'error') {
    datadogRum.addError(error, getDdLogProperties(level, source, undefined, contextArg));
  }
};

// *************************************************************
// Install Error Handlers
// *************************************************************

const installUnhandledExceptionForwarding = (sendToDataDog: SendToDataDogFunction): Cleanup => {
  const originalHandler = window.onerror;

  window.onerror = (
    event: Event | string,
    source?: string,
    lineno?: number,
    colno?: number,
    error?: Error,
  ) => {
    error ??= new Error(typeof event === 'string' ? event : 'Unknown error');
    sendToDataDog('error', 'unhandledException', [error, { source, lineno, colno }]);
    if (typeof originalHandler === 'function') {
      originalHandler(event, source, lineno, colno, error);
    }
  };

  return () => {
    window.onerror = originalHandler;
  };
};

const installUnhandledRejectionForwarding = (sendToDataDog: SendToDataDogFunction): Cleanup => {
  const originalHandler = window.onunhandledrejection;

  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    sendToDataDog('error', 'unhandledRejection', [normalizeUnknownError(event?.reason)]);
    if (typeof originalHandler === 'function') {
      originalHandler.call(window, event);
    }
  };

  return () => {
    window.onunhandledrejection = originalHandler;
  };
};

/**
 * Installs error forwarding to Datadog Logs.
 *
 * Forwards unhandled exceptions, unhandled rejections, console.error,
 * and console.warn calls.
 * Returns a cleanup function that restores the original handlers.
 */
export const installDatadogErrorForwarding = createInstallDatadogErrorForwardingFunction(
  installUnhandledExceptionForwarding,
  installUnhandledRejectionForwarding,
  sendToDataDog,
);
